// Haptic feedback for action buttons: deploy, redeploy, save, submit. Not for tabs, links or toggles.
//
//   <Haptic disabled={busy}><button className="btn btn-primary" disabled={busy} onClick={deploy}>Deploy</button></Haptic>
//
// On iOS an invisible native switch covers the button: the finger flips it, iOS plays its tick
// (the only way since iOS 26.5), and the click is passed on to the button. On Android the tap vibrates.
// For a refused action (a form that won't submit) call haptic("error") from ./native: a double tick.
// Toasts tick on their own (error tone: double), merged with the tap that caused them.

import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { haptic, isIOS, markTick } from './native';

/** Wraps one action control. `disabled` should mirror the control's, so a dead button stays silent. */
export function Haptic({ children, disabled, radius = 'var(--radius-field)', className = '' }: { children: ReactNode; disabled?: boolean; radius?: string; className?: string }) {
  return (
    <span className={`relative inline-flex ${className}`} onClickCapture={isIOS || disabled ? undefined : () => haptic()}>
      {children}
      <HapticSwitch radius={radius} disabled={disabled} />
    </span>
  );
}

/**
 * The bare overlay, for places a wrapper would break the markup (e.g. a daisyUI menu <li>):
 * put it inside a `relative` parent, after the control. Its click goes to the parent's first
 * link, button or input (or, inside a <label>, to the label's control).
 */
export function HapticSwitch({ radius, disabled }: { radius?: string; disabled?: boolean }) {
  if (!isIOS || disabled) return null;
  return (
    <input
      type="checkbox"
      {...{ switch: '' }}
      tabIndex={-1}
      aria-hidden="true"
      className="dr-haptic"
      style={radius ? ({ '--dr-haptic-radius': radius } as CSSProperties) : undefined}
      onClick={forward}
    />
  );
}

function forward(e: MouseEvent<HTMLInputElement>) {
  markTick(); // the finger just played it; a toast or error that follows won't repeat it
  const host = e.currentTarget.parentElement;
  const target =
    host instanceof HTMLLabelElement ? host.control : (host?.querySelector(":scope > :is(a, button, input, select, [role='button']):not(.dr-haptic)") as HTMLElement | null);
  if (target && target !== e.currentTarget) (target as HTMLElement).click();
}
